/**
 * Anti burn-in helpers.
 *
 * OLED and LCD panels develop permanent image retention when the same
 * pixels show identical content for hours. In DOOH that's any time the
 * player sits on:
 *   - IDLE screen ("Awaiting next campaign")
 *   - Pairing screen (QR code + 8-char code)
 *   - A long-running static image campaign
 *   - Diagnostic screen left up overnight
 *
 * Samsung signage panels have hardware-level pixel-shift features but
 * we apply a software guard on top for two reasons:
 *   1. They're off by default and vary by model — never assume.
 *   2. Our own UI shifts are bounded (≤4px) and aligned with the
 *      hardware shift schedule (~5 min) so we don't double-jitter.
 *
 * Usage — hook style, returns CSS to spread on a container:
 *
 *     const drift = usePixelDrift();
 *     return <div style={{ ...rootStyle, ...drift }}>…</div>;
 *
 * The hook returns `transform: translate(dx, dy)` + a 2s ease
 * transition; that's all. By spreading on YOUR root style you keep
 * full control over layout — no extra DOM node, no flex/grid surprises.
 *
 * Cost: a single transform-only animation on the GPU. Effectively free.
 */

import { useEffect, useState, type CSSProperties } from 'react';

const PIXEL_SHIFT_PERIOD_MS = 60_000; // change quadrant every minute
const MAX_OFFSET_PX = 3;

const QUADRANTS: ReadonlyArray<readonly [number, number]> = [
  [+MAX_OFFSET_PX, +MAX_OFFSET_PX],
  [-MAX_OFFSET_PX, +MAX_OFFSET_PX],
  [-MAX_OFFSET_PX, -MAX_OFFSET_PX],
  [+MAX_OFFSET_PX, -MAX_OFFSET_PX],
];

export function usePixelDrift(disabled = false): CSSProperties {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (disabled) return;
    const t = setInterval(
      () => setIdx((i) => (i + 1) % QUADRANTS.length),
      PIXEL_SHIFT_PERIOD_MS,
    );
    return () => clearInterval(t);
  }, [disabled]);

  if (disabled) return {};

  const [dx, dy] = QUADRANTS[idx];
  return {
    // 2s ease keeps the shift visually undetectable — the user never
    // sees motion, only the cumulative effect of pixel boundaries
    // moving 1 device pixel every several seconds.
    transition: 'transform 2s ease-in-out',
    transform: `translate(${dx}px, ${dy}px)`,
    // Force own compositing layer so the transform doesn't re-rasterise
    // children every shift — matters on 4K signage where re-paint cost
    // is non-trivial.
    willChange: 'transform',
  };
}
